import { useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';

/**
 * useJsApiLoader - Hook to load the Google Maps JavaScript API.
 * @param {object} props - The options to load the Google Maps JavaScript API.
 * @param {string} props.googleMapsApiKey - The Google Maps API key.
 * @returns {{
 * map: google.maps.Map | null,
 * autocomplete: google.maps.places.Autocomplete | null,
 * isLoaded: boolean,
 * onLoadAutocomplete: function(google.maps.places.Autocomplete): void,
 * onLoadMap: function(google.maps.Map): void
 * }} - The state of the API loading.
 */
const useGoogleMap = ({
  googleMapsApiKey,
}) => {
  /** @type {[google.maps.Map | null, React.Dispatch<React.SetStateAction<google.maps.Map | null>>]} */
  const [map, setMap] = useState(null);

  /** @type {[google.maps.places.Autocomplete | null, React.Dispatch<React.SetStateAction<google.maps.places.Autocomplete | null>>]} */
  const [autocomplete, setAutocomplete] = useState(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey,
    libraries: ['places', 'geometry'],
    region: 'FR',
    language: 'fr',
  });

  /**
   * onLoadAutocomplete - Function to set the Autocomplete instance when loaded.
   * @param {google.maps.places.Autocomplete} autocompleteInstance
   * @returns {void}
   */
  const onLoadAutocomplete = (autocompleteInstance) => {
    autocompleteInstance.setComponentRestrictions({ country: 'FR' });
    autocompleteInstance.setTypes(['address']);
    setAutocomplete(autocompleteInstance);
  };

  /**
   * onLoadMap - Function to set the Map instance when loaded.
   * @param {google.maps.Map} mapInstance
   * @returns {void}
   */
  const onLoadMap = (mapInstance) => {
    setMap(mapInstance);
  };

  return {
    map,
    autocomplete,
    isLoaded,
    onLoadAutocomplete,
    onLoadMap,
  };
};

export default useGoogleMap;
